import { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Float, Text, Box, Cylinder } from '@react-three/drei';
import * as THREE from 'three';

// Monitor component
function Monitor({ position }: { position: [number, number, number] }) {
  const meshRef = useRef<THREE.Group>(null);
  
  return (
    <group ref={meshRef} position={position}>
      {/* Screen */}
      <Box args={[1.6, 0.9, 0.05]} position={[0, 0, 0]}>
        <meshStandardMaterial color="#1a1a2e" emissive="#0f0f1a" emissiveIntensity={0.2} />
      </Box>
      {/* Screen glow */}
      <Box args={[1.5, 0.8, 0.06]} position={[0, 0, 0.03]}>
        <meshStandardMaterial color="#0d47a1" emissive="#1976d2" emissiveIntensity={0.5} />
      </Box>
      {/* Stand */}
      <Cylinder args={[0.08, 0.1, 0.4]} position={[0, -0.6, 0]}>
        <meshStandardMaterial color="#333" />
      </Cylinder>
      {/* Base */}
      <Cylinder args={[0.3, 0.35, 0.05]} position={[0, -0.8, 0]}>
        <meshStandardMaterial color="#333" />
      </Cylinder>
      {/* Code lines on screen */}
      {[...Array(5)].map((_, i) => (
        <Box key={i} args={[0.8 - i * 0.1, 0.02, 0.01]} position={[-0.2, 0.2 - i * 0.12, 0.04]}>
          <meshStandardMaterial color="#4ade80" emissive="#22c55e" emissiveIntensity={0.8} />
        </Box>
      ))}
    </group>
  );
}

// Keyboard component
function Keyboard({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      <Box args={[1.2, 0.04, 0.4]}>
        <meshStandardMaterial color="#2a2a3e" />
      </Box>
      {/* Keys */}
      {[...Array(4)].map((_, row) => 
        [...Array(12)].map((_, col) => (
          <Box 
            key={`${row}-${col}`} 
            args={[0.07, 0.02, 0.07]} 
            position={[-0.5 + col * 0.09, 0.03, -0.15 + row * 0.09]}
          >
            <meshStandardMaterial color="#444" />
          </Box>
        ))
      )}
    </group>
  );
}

// Mouse component
function Mouse({ position }: { position: [number, number, number] }) {
  const meshRef = useRef<THREE.Mesh>(null);
  
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
    }
  });
  
  return (
    <mesh ref={meshRef} position={position}>
      <Box args={[0.15, 0.08, 0.25]}>
        <meshStandardMaterial color="#333" />
      </Box>
    </mesh>
  );
}

// Desk component
function Desk({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      {/* Desktop */}
      <Box args={[3, 0.1, 1.5]}>
        <meshStandardMaterial color="#5d4037" />
      </Box>
      {/* Legs */}
      {[[-1.3, -0.7], [1.3, -0.7], [-1.3, 0.6], [1.3, 0.6]].map(([x, z], i) => (
        <Cylinder key={i} args={[0.05, 0.05, 1.4]} position={[x, -0.7, z]}>
          <meshStandardMaterial color="#4a3728" />
        </Cylinder>
      ))}
    </group>
  );
}

// Floating code symbols
function FloatingSymbols() {
  const symbols = useMemo(() => ['{ }', '</>', '[]', '&&', '||', '==', '++', '//'], []);
  
  return (
    <>
      {symbols.map((symbol, i) => (
        <Float
          key={i}
          speed={2}
          rotationIntensity={0.5}
          floatIntensity={0.5}
          position={[
            (Math.random() - 0.5) * 6,
            (Math.random() - 0.5) * 3 + 1,
            (Math.random() - 0.5) * 2 - 1
          ]}
        >
          <Text
            fontSize={0.15}
            color="#F97316"
            anchorX="center"
            anchorY="middle"
            font="https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYAZ9hjp-Ek-_EeA.woff2"
          >
            {symbol}
          </Text>
        </Float>
      ))}
    </>
  );
}

// Floating particles
function Particles() {
  const count = 30;
  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 10;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 6;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 4;
    }
    return pos;
  }, []);

  const pointsRef = useRef<THREE.Points>(null);

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y = state.clock.elapsedTime * 0.05;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.05}
        color="#F97316"
        transparent
        opacity={0.6}
        sizeAttenuation
      />
    </points>
  );
}

// Main scene
function Scene() {
  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 5, 5]} intensity={0.8} castShadow />
      <pointLight position={[-5, 3, -5]} intensity={0.5} color="#F97316" />
      <pointLight position={[5, 3, 5]} intensity={0.3} color="#3b82f6" />
      
      <Desk position={[0, -1, 0]} />
      <Monitor position={[0, 0.2, -0.3]} />
      <Keyboard position={[0, -0.45, 0.3]} />
      <Mouse position={[1, -0.4, 0.4]} />
      
      {/* Books */}
      <group position={[-1, -0.35, -0.2]}>
        <Box args={[0.3, 0.4, 0.05]} position={[0, 0, 0]}>
          <meshStandardMaterial color="#dc2626" />
        </Box>
        <Box args={[0.28, 0.35, 0.05]} position={[0.05, 0.02, 0.06]}>
          <meshStandardMaterial color="#2563eb" />
        </Box>
        <Box args={[0.32, 0.38, 0.05]} position={[-0.03, 0.01, 0.12]}>
          <meshStandardMaterial color="#16a34a" />
        </Box>
      </group>
      
      <FloatingSymbols />
      <Particles />
      
      <OrbitControls 
        enableZoom={false} 
        enablePan={false}
        maxPolarAngle={Math.PI / 2}
        minPolarAngle={Math.PI / 3}
        autoRotate
        autoRotateSpeed={0.5}
      />
    </>
  );
}

export default function ComputerLab() {
  return (
    <div className="w-full h-[500px] md:h-[600px]">
      <Canvas camera={{ position: [0, 2, 5], fov: 50 }} gl={{ antialias: true }}>
        <Scene />
      </Canvas>
    </div>
  );
}